import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";

const LOGO_URL = "/logo.png";

const NAV_LINKS = [
    { label: "Home", href: "/", type: "route" },
    { label: "Features", href: "/#features", type: "hash" },
    { label: "How it Works", href: "/#how-it-works", type: "hash" },
    { label: "My Story", href: "/my-story", type: "route" },
    { label: "Directory", href: "/directory", type: "route" },
    // { label: "About", href: "/#about", type: "hash" },
    { label: "Blog/Resources", href: "/#resources", type: "hash" },
    { label: "Pricing", href: "/#pricing", type: "hash" },
    { label: "Contact", href: "/#contact", type: "hash" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const handleRouteClick = () => {
        setOpen(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <header
            data-testid="site-navbar"
            className="fixed top-0 left-0 right-0 z-50 border-b border-slate-200/70 bg-white/95 backdrop-blur-2xl shadow-[0_8px_30px_rgba(15,23,42,0.06)]"
        >
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="h-20 flex items-center justify-between gap-3 xl:gap-4 2xl:gap-6">
                    <Link
                        to="/"
                        data-testid="nav-logo-link"
                        className="flex items-center shrink-0"
                        onClick={() => setOpen(false)}
                    >
                        <img
                            src={LOGO_URL}
                            alt="Genesis Recovery AI"
                            className="h-[4.4rem] w-[12.4rem] 2xl:h-[5.1rem] 2xl:w-[14.5rem] max-w-none object-contain object-left contrast-125 saturate-110"
                        />
                    </Link>

                    <nav className="hidden xl:flex flex-1 justify-center min-w-0 px-1 2xl:px-2">
                        <div className="flex items-center gap-0 rounded-full border border-slate-200 bg-slate-50/90 px-1.5 2xl:px-2 py-1 shadow-sm">
                            {NAV_LINKS.map((link) =>
                                link.type === "route" ? (
                                    <NavLink
                                        key={link.label}
                                        to={link.href}
                                        end={link.href === "/"}
                                    onClick={handleRouteClick}
                                        data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                                        className={({ isActive }) =>
                                            `whitespace-nowrap px-2.5 2xl:px-3 py-2 text-[14px] 2xl:text-[16.5px] leading-none font-semibold rounded-full transition-colors ${
                                                isActive
                                                    ? "bg-white text-slate-950 shadow-sm"
                                                    : "text-slate-600 hover:text-slate-950"
                                            }`
                                        }
                                    >
                                        {link.label}
                                    </NavLink>
                                ) : (
                                    <a
                                        key={link.label}
                                        href={link.href}
                                        onClick={() => setOpen(false)}
                                        data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                                    className="whitespace-nowrap px-2.5 2xl:px-3 py-2 text-[14px] 2xl:text-[16.5px] leading-none font-semibold rounded-full text-slate-600 hover:text-slate-950 transition-colors"
                                    >
                                        {link.label}
                                    </a>
                                )
                            )}
                        </div>
                    </nav>

                    <div className="hidden xl:flex items-center gap-2 shrink-0">
                        <a
                            href="/#download"
                            data-testid="navbar-get-app-button"
                            className="inline-flex items-center gap-1.5 h-10 px-3.5 2xl:px-4 rounded-full text-[12.5px] 2xl:text-[14px] font-semibold transition-colors bg-slate-950 text-white hover:bg-slate-800 shadow-sm"
                        >
                            Get the App
                            <ArrowRight className="w-4 h-4" />
                        </a>
                    </div>

                    <button
                        data-testid="navbar-mobile-toggle"
                        className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl border border-slate-200 text-slate-900 bg-white transition-colors shadow-sm"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            {open && (
                <div data-testid="navbar-mobile-menu" className="xl:hidden border-t border-slate-200 bg-white">
                    <nav className="max-w-[1600px] mx-auto px-4 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
                        {NAV_LINKS.map((link) => (
                            link.type === "route" ? (
                                <NavLink
                                    key={link.label}
                                    to={link.href}
                                    end={link.href === "/"}
                                    onClick={handleRouteClick}
                                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                                    className="px-3 py-2.5 text-slate-900 text-[17px] rounded-md hover:bg-slate-50"
                                >
                                    {link.label}
                                </NavLink>
                            ) : (
                                <a
                                    key={link.label}
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                                    className="px-3 py-2.5 text-slate-900 text-[17px] rounded-md hover:bg-slate-50"
                                >
                                    {link.label}
                                </a>
                            )
                        ))}
                        <a
                            href="/#download"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-get-app-button"
                            className="mt-2 inline-flex justify-center items-center gap-2 bg-slate-950 text-white rounded-full h-11 px-4 text-[15px] font-semibold"
                        >
                            Get the App
                        </a>
                    </nav>
                </div>
            )}
        </header>
    );
}
